import { observable, makeObservable, computed } from 'mobx'

import { Id } from 'types/definitions'
import { Model } from 'infra/model'
import { DateZero, maybeParseDate } from 'utils/date-util'

import {
  BankAccountDetailsDto,
  CheckTaxRefundDeliveryMethodDto,
  DirectDepositTaxRefundDeliveryMethodDto,
  TaxRefundMethodVersionDto,
} from './dtos'

import {
  TaxRefundType as TaxRefundTypeProto,
  TaxRefundDeliveryMethod as TaxRefundDeliveryMethodProto,
  BankAccountType as BankAccountTypeProto,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'

import { PhysicalAddress } from '../PhysicalAddress'
import { timestampToISO } from 'utils/grpc-util'
import { PhysicalAddressDto } from '../dtos'

export enum TaxRefundType {
  Personal = 'Personal',
  Business = 'Business',
  Unspecified = 'Unspecified',
}

type TaxRefundDeliveryMethod =
  | CheckTaxRefundDeliveryMethod
  | DirectDepositTaxRefundDeliveryMethod
  | FutureTaxCreditTaxRefundDeliveryMethod

export class TaxRefundMethodVersion extends Model<
  TaxRefundMethodVersionDto,
  TaxRefundMethodVersion
> {
  @observable
  id = Id.none

  @observable
  version = 0

  @observable
  type?: TaxRefundType

  @observable
  deliveryMethod?: TaxRefundDeliveryMethod

  @observable
  dateCreated = DateZero()

  @observable
  dateUpdated = DateZero()

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<TaxRefundMethodVersionDto>,
  ): Partial<TaxRefundMethodVersion> {
    return {
      id: attrs.id,
      version: attrs.version,
      type: attrs.refundType ? toTaxRefundType(attrs.refundType) : undefined,
      deliveryMethod: attrs.deliveryMethod
        ? toDeliveryMethod(attrs.deliveryMethod)
        : undefined,
      dateCreated:
        maybeParseDate(timestampToISO(attrs.createTime)) || DateZero(),
      dateUpdated:
        maybeParseDate(timestampToISO(attrs.updateTime)) || DateZero(),
    }
  }
}

export class CheckTaxRefundDeliveryMethod extends Model<
  CheckTaxRefundDeliveryMethodDto,
  CheckTaxRefundDeliveryMethod
> {
  @observable
  mailingAddress?: PhysicalAddress

  @computed
  get typeDisplay() {
    return 'Check'
  }

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<CheckTaxRefundDeliveryMethodDto>,
  ): Partial<CheckTaxRefundDeliveryMethod> {
    return {
      mailingAddress: attrs.mailingAddress
        ? toPhysicalAddress(attrs.mailingAddress)
        : undefined,
    }
  }
}

export class DirectDepositTaxRefundDeliveryMethod extends Model<
  DirectDepositTaxRefundDeliveryMethodDto,
  DirectDepositTaxRefundDeliveryMethod
> {
  @observable
  bankAccount?: BankAccountDetails

  @computed
  get typeDisplay() {
    return 'Direct deposit'
  }

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<DirectDepositTaxRefundDeliveryMethodDto>,
  ): Partial<DirectDepositTaxRefundDeliveryMethod> {
    return {
      bankAccount: attrs.bankAccount
        ? new BankAccountDetails().set(attrs.bankAccount, { parse: true })
        : undefined,
    }
  }
}

enum BankAccountType {
  Unspecified = 'Unspecified',
  Checking = 'Checking',
  Savings = 'Savings',
}

class BankAccountDetails extends Model<
  BankAccountDetailsDto,
  BankAccountDetails
> {
  @observable
  accountType: BankAccountType = BankAccountType.Unspecified

  @observable
  accountNumber: string = ''

  @observable
  routingNumber: string = ''

  @observable
  bankName?: string

  @computed
  get accountNumberDisplay() {
    return this.accountNumber.replace(/\d/g, '*')
  }

  @computed
  get routingNumberDisplay() {
    return this.routingNumber.replace(/\d/g, '*')
  }

  @computed
  get bankNameDisplay() {
    return this.bankName?.replace(/\S/g, '*') ?? ''
  }

  constructor() {
    super()
    makeObservable(this)
  }

  parse(attrs: BankAccountDetailsDto): Partial<BankAccountDetails> {
    return {
      accountType: attrs.accountType
        ? toBankAccountType(attrs.accountType)
        : BankAccountType.Unspecified,
      accountNumber: attrs.accountNumber,
      routingNumber: attrs.routingNumber,
      bankName: attrs.bankName,
    }
  }
}

export class FutureTaxCreditTaxRefundDeliveryMethod {
  @computed
  get typeDisplay() {
    return 'Future tax credit'
  }
}

function toTaxRefundType(type: TaxRefundTypeProto): TaxRefundType {
  switch (type) {
    case TaxRefundTypeProto.PERSONAL:
      return TaxRefundType.Personal
    case TaxRefundTypeProto.BUSINESS:
      return TaxRefundType.Business
    default:
      return TaxRefundType.Unspecified
  }
}

function toDeliveryMethod(
  deliveryMethod: TaxRefundDeliveryMethodProto.AsObject,
) {
  if (deliveryMethod.check) {
    return new CheckTaxRefundDeliveryMethod().set(deliveryMethod.check, {
      parse: true,
    })
  }

  if (deliveryMethod.directDeposit) {
    return new DirectDepositTaxRefundDeliveryMethod().set(
      deliveryMethod.directDeposit,
      {
        parse: true,
      },
    )
  }

  if (deliveryMethod.futureTaxCredit) {
    return new FutureTaxCreditTaxRefundDeliveryMethod()
  }

  return undefined
}

function toBankAccountType(type: BankAccountTypeProto): BankAccountType {
  switch (type) {
    case BankAccountTypeProto.SAVINGS:
      return BankAccountType.Savings
    case BankAccountTypeProto.CHECKING:
      return BankAccountType.Checking
    case BankAccountTypeProto.BANK_ACCOUNT_TYPE_UNSPECIFIED:
      return BankAccountType.Unspecified
  }
}

function toPhysicalAddress(dto: PhysicalAddressDto): PhysicalAddress {
  return {
    addressLine1: dto.addressLine1,
    addressLine2: dto.addressLine2,
    city: dto.city,
    postalCode: dto.postalCode,
    region: dto.region,
    country: dto.country,
  }
}
